import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription, Observable, filter } from 'rxjs';
import { AppState } from 'src/app/store/app.reducer';
import { TASKS_PAGE_DEFAULT_SIZE, TASKS_PAGE_SIZE_OPTIONS, TASKS_TABLE_HEADER, ETaskStatus } from 'src/app/conf/tasks';
import { PaginationParams } from 'src/app/model/api/pagination-params';
import { Task, TaskState, TaskType, getTaskResult, getTaskProgress } from 'src/app/model/tasks';
import * as TaskActions from 'src/app/store/task/task.actions';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { selectActiveTask, selectTaskStates, selectTaskTypes, selectTasks } from 'src/app/store/task/task.selector';
import { TableLazyLoadEvent } from 'primeng/table';
import { pageSortChange } from 'src/app/services/table';
import { StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';

@Component({
	selector: 'app-tasks-dialog',
	templateUrl: './tasks-dialog.component.html',
	providers: [],
})

export class TasksDialogComponent implements OnInit, OnDestroy {
	tasks$: Observable<Task[] | undefined>;
	tasksSub: Subscription | undefined;
	activeTasks$: Observable<Task[] | undefined>;
	activeTasksSub: Subscription | undefined;
	taskStates$: Observable<TaskState[] | undefined>;
	taskStatesSub: Subscription | undefined;
	taskTypes$: Observable<TaskType[] | undefined>;
	taskTypesSub: Subscription | undefined;
	states: TaskState[];
	types: TaskType[];
	tasks: Task[];
	activeTasks: Task[];

	pagingSub: Subscription;
	pageChangeSub: Subscription;
	paging: PaginationParams = {
		size: TASKS_PAGE_DEFAULT_SIZE,
		page: 1,
		sort: 'updated_at',
		order: 'desc',
	};
	pageSize: number = TASKS_PAGE_DEFAULT_SIZE;
	pageSizeOptions = TASKS_PAGE_SIZE_OPTIONS;
	taskColumns = TASKS_TABLE_HEADER;
	resultLength: number;

	tasksLoading: boolean;

	// design
	screenWidth: number;
	MIN_MD_WIDTH = 500;
	COLUMN_TITLE_MIN_WD = 647;
	acceptButtonStyle: string;
	rejectButtonStyle: string;

	constructor(
		private store: Store<AppState>,
		private ref: DynamicDialogRef,
		public confirmationService: ConfirmationService,
		private translate: TranslateService,
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		this.tasks$ = this.store.select(selectTasks);
		this.activeTasks$ = this.store.select(selectActiveTask);
		this.taskStates$ = this.store.select(selectTaskStates);
		this.taskTypes$ = this.store.select(selectTaskTypes);
	}

	ngOnInit(): void {
		this.tasksSub = this.tasks$.subscribe(tasks => {
			this.tasks = tasks;
			this.tasksLoading = false;
		});
		this.tasksLoading = true;
		this.activeTasksSub = this.activeTasks$.subscribe(activeTasks => {
			if (this.activeTasks !== undefined && activeTasks !== undefined && this.activeTasks.length > 0 && activeTasks.length === 0) {
				this.getTasks();
			}
			this.activeTasks = activeTasks;
		});

		this.store.dispatch(TaskActions.getTaskStates());
		this.store.dispatch(TaskActions.getTaskTypes());
		this.taskStatesSub = this.taskStates$.subscribe(states => {
			this.states = states;
		});
		this.taskTypesSub = this.taskTypes$.subscribe(types => {
			this.types = types;
		});

		this.setOnTasksRefresh();
		this.onResize();
	}

	ngOnDestroy(): void {
		this.tasksSub?.unsubscribe();
		this.taskStatesSub?.unsubscribe();
		this.taskTypesSub?.unsubscribe();
		this.activeTasksSub?.unsubscribe();
	}

	setOnPagiangChanges(): void {
		/*this.pageChangeSub = this.paginator.page.subscribe((pageEvent: PageEvent) => {
			this.paging = {
				...this.paging,
				page: pageEvent.pageIndex + 1,
				size: pageEvent.pageSize || this.pageSize,
			};
		});*/
	}

	loadTasks(event: TableLazyLoadEvent) {

		this.paging = pageSortChange(this.paging, event);

		this.getTasks();
	}


	getTasks(): void {
		this.tasksLoading = true;
		this.store.dispatch(TaskActions.getTasks({ paging: this.paging }));
	}

	setOnTasksRefresh(): void {
		this.pagingSub = this.store.select('task', 'itemsPagination')
			.pipe(filter(p => !!p))
			.subscribe(
				paging => {
					this.resultLength = paging.TotalCount;
				}
			);
	}

	getResult(task: Task): string {
		return getTaskResult(task);
	}

	getProgress(task: Task): number {
		return Math.floor(getTaskProgress(task));
	}

	isRunning(task: Task): boolean {
		return getTaskProgress(task) !== 100 && (task.Status === ETaskStatus.RUNNING || task.Status === ETaskStatus.PENDING);
	}

	isSuccess(task: Task): boolean {
		return getTaskProgress(task) === 100 && task.Status === ETaskStatus.SUCCESS;
	}

	isAbortOrError(task: Task): boolean {
		return task.Status === ETaskStatus.ABORTED || task.Status === ETaskStatus.ERROR;
	}

	getTaskStatusSeverity(task: Task): string {
		switch (task.Status) {
			case ETaskStatus.RUNNING:
				return 'info';
			case ETaskStatus.PENDING:
				return 'info';
			case ETaskStatus.SUCCESS:
				return 'success';
			case ETaskStatus.ABORTED:
				return 'warning';
			case ETaskStatus.ERROR:
				return 'danger';
			default:
				return 'info';
		}
	}

	getTaskStatus(code: number): string {
		const taskState = this.states.find(s => s.Id === code);
		return taskState?.Description;
	}

	getTaskType(type: number): string {
		const taskType = this.types.find(s => s.Id === type);
		return taskType?.Description;
	}

	cannotAbort(task: Task) {
		return !this.isRunning(task) || task.Status === ETaskStatus.ERROR;
	}

	abortTask(task: Task): void {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('CANCEL_TASK'),
			message: this.translate.instant('CANCEL_TASK_WARNING_NEW'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.store.dispatch(TaskActions.abortTask({ taskId: task.Id }));
			},
			reject: () => {
			}
		});
	}

	closeDialog(): void {
		this.ref.close();
	}


	// Design
	@HostListener('window:resize')
	onResize(): void {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	underTablet(): boolean {
		return this.screenWidth <= 960;
		// return StandardPageLayout.underTablet(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}
}
